import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BallotSearchService } from './shared/services/ballot-search.service';
import { IBallotSearchRequest } from './shared/models/search/ballot-search-filter';
import { IBallotSearchResult } from './shared/models/search/ballot-search-result';
import { IPaginatedCollection } from '../../shared/models/paginated-collection';
import { combineLatest, debounceTime, distinctUntilChanged, forkJoin, merge, Observable, Subject, Subscription, switchMap, takeWhile, tap } from 'rxjs';
import { ITableColumn, TableColumnType } from '../controls/table/table-column';
import { AuthService } from '../../shared/services/auth.service';
import { ButtonVoteNowComponent } from './shared/components/button-vote-now/button-vote-now.component';
import { UserHasVotedComponent } from './user-has-voted/user-has-voted.component';
import { materialSymbols } from '../../shared/constants/material-symbols';
import { IExportSchema } from '../controls/models/export-to-excel';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { UserUtility } from '../../shared/utilities/user-utility';
import { BallotStatusBadgeComponent } from '../controls/status-badge/ballot-status-badge/ballot-status-badge.component';
import { BallotTypeBadgeComponent } from '../controls/status-badge/ballot-type-badge/ballot-type-badge.component';
import { IUser } from '../../shared/models/user';
import { ITableFilter } from '../controls/table/table-filter';
import { IBallotLegend } from './shared/models/ballot-legend';
import { FormControl, FormGroup } from '@angular/forms';
import { IApiResponse } from '../../shared/models/api-response';
import { appConstants } from '../../shared/constants/app-constants';
import { BallotType } from '../../shared/enums/ballot-type-enum';
import { UnifiedBallotStatus } from '../../shared/enums/unified-ballot-status-enum';
import { arraysAreIdentical } from '../../shared/utilities/array-utility';
import { YellowDashComponent } from './shared/components/yellow-dash/yellow-dash.component';
import { SamlService } from '../../shared/services/saml.service';
import { BallotDecisionConstants } from '../../shared/constants/ballot-constants';
import { ToastService } from '../../shared/services/toast.service';
import { ToastMessage, BallotTypes } from '../../shared/constants/toast-constants';
import { Sort } from '@angular/material/sort';
import _ from 'lodash';
import { DateFormatPipe } from '../controls/pipes/date-format.pipe';
import { SrsBallotReviewStage } from './shared/models/sr/srs-ballot-review-stage';
import { BallotReviewStageBadgeComponent } from './shared/components/ballot-review-stage-badge/ballot-review-stage-badge.component';
@Component({
    selector: 'app-ballots',
    templateUrl: './ballots.component.html',
    styleUrl: './ballots.component.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class BallotsComponent implements OnInit, OnDestroy {
    @ViewChild('popover') popover!: NgbPopover;

    info = materialSymbols.info;
    download = materialSymbols.download;
    close = materialSymbols.close;
    searchIcon = materialSymbols.search;

    loading: boolean = false;
    subscriptions: Subscription = new Subscription();
    paginatedCollection: IPaginatedCollection<IBallotSearchResult> | null = null;

    ballotSelectionType = {
        allBallots: 'allBallots',
        myBallot: 'myBallot'
    }

    searchForm = new FormGroup({
        searchTermCtrl: new FormControl<string | null>(''),
        ballotSelectionCtrl: new FormControl(this.ballotSelectionType.myBallot),
        ballotTypeCtrl: new FormControl<BallotType[] | null>([]),
        ballotStatusCtrl: new FormControl<UnifiedBallotStatus[] | null>([]),
        ballotReviewStageCtrl: new FormControl<(SrsBallotReviewStage | null)[] | null>([])
    })
    selectedBallotTypes: BallotType[] = [];
    selectedBallotStatuses: UnifiedBallotStatus[] = [];

    selectMultipleBallotTypeViewLoaded: Subject<void> = new Subject<void>();
    selectMultipleBallotStatusViewLoaded: Subject<void> = new Subject<void>();
    selectMultipleBallotReviewStageViewLoaded: Subject<void> = new Subject<void>();


    tableData: IBallotSearchResult[] = [];
    exportData: IBallotSearchResult[] = [];
    tableColumns: ITableColumn<IBallotSearchResult>[] = []

    isUserPmOrAbove: boolean = false;
    isUserPmTw: boolean = false;
    isUserHoSD: boolean = false;
    isUserCommitteeMember: boolean = false;
    isUserSrt: boolean = false;
    isUserInternalUser: boolean = false;
    isInternalPublicUser: boolean = false;

    currentUser: IUser | null = null;
    currentUserCommitteeCodes: string[] = [];
    isHiddenAssignedToAndDecision: boolean = true;

    sort!: Sort;

    ballotLegends: IBallotLegend[] = [
        {
            name: 'NPB',
            description: 'National Project Ballot'
        },
        {
            name: 'CIB',
            description: 'Committee Internal Ballot'
        },
        {
            name: 'SR',
            description: 'Systematic Review'
        },
        {
            name: 'IEC',
            description: 'International Electrotechnical Commission'
        }
    ]

    exportSchema: IExportSchema<IBallotSearchResult>[] = [];

    tableFilters: ITableFilter[] = [];

    constructor(
        public _authService: AuthService,
        private _router: Router,
        private _ballotSearchService: BallotSearchService,
        private _samlService: SamlService,
        private _toastService: ToastService,
        private dateFormatPipe: DateFormatPipe,
    ) { }

    ngOnInit(): void {
        this.initUser();
        this.initialiseOnSearchFormChanged();
        this.performInitialSearch();
    }


    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    initUser() {
        const currentUserObs$ = this._authService.currentUserSubject
        .pipe(
            takeWhile(() => this.currentUser == null)
        );

        const isHoSDObs$ = this._samlService.getLoggedInUserHoSDStatus();

        const initUserSubscription = combineLatest([currentUserObs$, isHoSDObs$]).subscribe({
            next:([user, isHosdResponse]) => {
                if (user) {
                    this.currentUser = user;
                    this.isUserPmOrAbove = UserUtility.userIsPmOrAbove(this.currentUser);
                    this.isUserPmTw = UserUtility.userIsPmTw(this.currentUser);
                    this.currentUserCommitteeCodes = this.isUserPmTw ? UserUtility.getCommitteeCodes(this.currentUser) : [];
                    this.isUserCommitteeMember = UserUtility.isCommitteeMember(this.currentUser);
                    this.isUserSrt = UserUtility.hasRoles(this.currentUser, appConstants.userRole.moderator);
                    this.isUserHoSD = isHosdResponse.success && isHosdResponse.content !== undefined ? isHosdResponse.content : false;
                    this.isUserInternalUser = UserUtility.isInternalUser(this.currentUser);
                    this.isInternalPublicUser = UserUtility.isInternalPublicUser(this.currentUser);

                    if (!this.isUserInternalUser) {
                        // Review Stage select control is not visible to external users.  
                        // So there is no need to load its data
                        this.onSelectMultipleBallotReviewStageViewLoaded();
                    }

                    this.initialiseTableColumns();
                    this.initExportSchema();
                }
            },
            error: (error) => {
                console.error('BallotsComponent: Error initializing user:', error);
            }
        });

        this.subscriptions.add(initUserSubscription);
    }

    initialiseTableColumns() {
        const standardColumns: ITableColumn<IBallotSearchResult>[] = [
            {
                name: 'ballotType',
                displayName: 'Ballot Type',
                isSortable: true,
                type: TableColumnType.component,
                component: BallotTypeBadgeComponent,
                getComponentInput: (row: IBallotSearchResult) => {
                    return { ballotType: row.ballotType };
                },
            },
            {
                name: 'reference',
                displayName: 'Reference',
                type: TableColumnType.routerLink,
                getLink: (row: IBallotSearchResult) => {
                const ballotType = row.ballotType.toLowerCase() == 'sr' ? 'srs' : row.ballotType.toLowerCase();
                return `/ballots/${ballotType}/${row.ballotId}`;
                },
                isSortable: true,
            },
            {
                name: 'statusId',
                displayName: 'Status Id',
                isSortable: true,
                isHidden: true,
            },
            {
                name: 'statusName',
                displayName: 'Status',
                isSortable: true,
                type: TableColumnType.component,
                component: BallotStatusBadgeComponent,
                getComponentInput: (row: IBallotSearchResult) => {
                    return { statusId: row.statusId };
                },
            },
            {
                name: 'reviewStageId',
                displayName: 'Review Stage Id',
                type: TableColumnType.string,
                isSortable: true,
                isHidden: true
            },
            {
                name: 'reviewStageDisplayText',
                displayName: 'Review Stage',
                type: TableColumnType.component,
                component: BallotReviewStageBadgeComponent,
                getComponentInput: (row: IBallotSearchResult) => {
                    return {
                        reviewStageId: row.reviewStageId !== SrsBallotReviewStage.ReadyForReviewSubmission ? row.reviewStageId : null,
                        reviewStageDisplayText: row.reviewStageId !== SrsBallotReviewStage.ReadyForReviewSubmission ? row.reviewStageDisplayText : null
                    };
                },
                isSortable: false,
                isHidden: !(this.isUserInternalUser)
            },
            {
                name: 'reviewStageDate',
                displayName: 'Date Assigned',
                type: TableColumnType.component,
                component: YellowDashComponent,
                getComponentInput: (row: IBallotSearchResult) => {
                    return { value: row.reviewStageDate ? this.dateFormatPipe.transform(row.reviewStageDate) : null };
                },
                isSortable: false,
                isHidden: !(this.isUserInternalUser)
            },
            {
                name: 'committeeCode',
                displayName: 'Committee',
                type: TableColumnType.routerLink,
                isSortable: true,
                getLink: (row: IBallotSearchResult) => `/committees/${row.committeeCode}`
            },
            {
                name: 'createdDate',
                displayName: 'Created Date',
                type: TableColumnType.date,
                isSortable: true
            },
            {
                name: 'openingDate',
                displayName: 'Opening Date',
                type: TableColumnType.date,
                isSortable: true
            },
            {
                name: 'closingDate',
                displayName: 'Closing Date',
                type: TableColumnType.date,
                isSortable: true
            },
            {
                name: 'decision',
                displayName: 'Decision',
                type: TableColumnType.component,
                component: YellowDashComponent,
                getComponentInput: (row: IBallotSearchResult) => {
                    return { value: this.transformDecision(row.decision) };
                },
                isSortable: false,
                isHidden: !(this.isUserInternalUser)
            },
            {
                name: 'lastUpdatedDate',
                displayName: 'Last Updated Date',
                type: TableColumnType.date,
                isSortable: true,
                isHidden: true
            },
            {
                name: 'hasCurrentUserVoted',
                displayName: 'Voted?',
                type: TableColumnType.component,
                isSortable: false,
                isHidden: !this.isUserCommitteeMember,
                component: UserHasVotedComponent,
                getComponentInput: (row: IBallotSearchResult) => {
                    return { ballot: row };
                }
            },
            {
                name: 'isCurrentUserEligibleVoter',
                displayName: 'Vote Now',
                type: TableColumnType.component,
                isSortable: false,
                isHidden: !this.isUserCommitteeMember,
                component: ButtonVoteNowComponent,
                getComponentInput: (row: IBallotSearchResult) => {
                    return { ballot: row };
                }
            },
        ];

        this.tableColumns = standardColumns;
    }

    initExportSchema() {
        const exportColumns: IExportSchema<IBallotSearchResult>[] =
        [
            {
                propertyName: 'ballotType',
                header: 'Ballot Type',
            },
            {
                propertyName: 'reference',
                header: 'Reference'
            },
            {
                propertyName: 'status',
                header: 'Status'
            },
            {
                propertyName: 'reviewStageDisplayText',
                header: 'Review Stage',
                valueRenderer: (row: IBallotSearchResult) => row.reviewStageId !== SrsBallotReviewStage.ReadyForReviewSubmission ? row.reviewStageDisplayText : ''
            },
            {
                propertyName: 'reviewStageDate',
                header: 'Date Assigned'
            },
            {
                propertyName: 'committeeCode',
                header: 'Committee'
            },
            {
                propertyName: 'createdDate',
                header: 'Created Date'
            },
            {
                propertyName: 'openingDate',
                header: 'Opening Date'
            },
            {
                propertyName: 'closingDate',
                header: 'Closing Date'
            },
{
                propertyName: 'closingDate',
                header: 'Closing Date'
            },
            {
                propertyName: 'decision',
                header: 'Decision',
                valueRenderer: (row: IBallotSearchResult) => this.transformDecision(row.decision)
            },
        ];

        this.exportSchema = exportColumns;
    }

    initialiseOnSearchFormChanged(): void {
        const searchObs$ = this.searchForm.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged((prev, curr) => this.isSearchFormTheSame(prev, curr)),
            tap((searchFormData: Partial<ISearchFormData>) => {
                // Trigger SelectMultipleBallotStatus and SelectMultipleBallotReviewStage to update its options
                this.selectedBallotTypes = searchFormData?.ballotTypeCtrl ?? [];
                this.selectedBallotStatuses = searchFormData?.ballotStatusCtrl ?? [];
            }),
            switchMap((searchFormData: Partial<ISearchFormData>) => {
                const obs$ = this.buildSearchObservable(searchFormData);
                return obs$;
            })
        );

        this.subscribeSearchResults(searchObs$);
    }

    isSearchFormTheSame(prev: Partial<ISearchFormData>, curr: Partial<ISearchFormData>): boolean {
        let result =
            ((prev.ballotTypeCtrl === null && curr.ballotTypeCtrl === null)
                || ((prev.ballotTypeCtrl !== null && curr.ballotTypeCtrl !== null)
                    && arraysAreIdentical<BallotType>(prev.ballotTypeCtrl!, curr.ballotTypeCtrl!)))
            &&
            ((prev.ballotStatusCtrl === null && curr.ballotStatusCtrl === null)
                || ((prev.ballotStatusCtrl !== null && curr.ballotStatusCtrl !== null)
                    && arraysAreIdentical<UnifiedBallotStatus>(prev.ballotStatusCtrl!, curr.ballotStatusCtrl!)))
            &&
            ((prev.ballotReviewStageCtrl === null && curr.ballotReviewStageCtrl === null)
                || ((prev.ballotReviewStageCtrl !== null && curr.ballotReviewStageCtrl !== null)
                    && arraysAreIdentical<SrsBallotReviewStage | null>(prev.ballotReviewStageCtrl!, curr.ballotReviewStageCtrl!)))

            &&
            (prev.searchTermCtrl === curr.searchTermCtrl)

            &&
            (prev.ballotSelectionCtrl === curr.ballotSelectionCtrl)
            ;
        return result;
    }

    transformDecision(decision: string | null | undefined): string {
      switch (decision) {
        case 'SVT_WITHDRAW':
          return BallotDecisionConstants.SVT_WITHDRAW;
        case 'SVT_RECONFIRM':
          return BallotDecisionConstants.SVT_RECONFIRM;
        case 'SVT_REVISE':
          return BallotDecisionConstants.SVT_REVISE;
        default:
          return BallotDecisionConstants.DEFAULT;
      }
    }

    getDefaultSearchFilter(): Partial<ISearchFormData> {
        // Construct default empty filter.
        let defaultFilter: Partial<ISearchFormData> = {
            ballotSelectionCtrl: this.ballotSelectionType.myBallot,
        };
        return defaultFilter;
    }

    performInitialSearch(): void {
        let searchFilter = this.getDefaultSearchFilter();

        const savedFilters = localStorage.getItem(appConstants.localStorageKey.ballotSearch);
        if (savedFilters) {
            searchFilter = JSON.parse(savedFilters);
        }

        // Wait until multiple select components are initialised before performing initial search
        forkJoin({
            selectMultipleBallotTypeViewLoaded: this.selectMultipleBallotTypeViewLoaded,
            selectMultipleBallotStatusViewLoaded: this.selectMultipleBallotStatusViewLoaded,
            selectMultipleBallotReviewStageViewLoaded: this.selectMultipleBallotReviewStageViewLoaded,
        })
        .subscribe(({ selectMultipleBallotTypeViewLoaded, selectMultipleBallotStatusViewLoaded, selectMultipleBallotReviewStageViewLoaded }) => {
            this.searchForm.patchValue(searchFilter);
        });
    }

    onSelectMultipleBallotTypeViewLoaded(): void {
        this.selectMultipleBallotTypeViewLoaded.next();
        this.selectMultipleBallotTypeViewLoaded.complete();
    }
    onSelectMultipleBallotStatusViewLoaded(): void {
        this.selectMultipleBallotStatusViewLoaded.next();
        this.selectMultipleBallotStatusViewLoaded.complete();
    }
    onSelectMultipleBallotReviewStageViewLoaded(): void {
        this.selectMultipleBallotReviewStageViewLoaded.next();
        this.selectMultipleBallotReviewStageViewLoaded.complete();
    }

    buildSearchObservable(searchFilter: Partial<ISearchFormData>): Observable<IApiResponse<IPaginatedCollection<IBallotSearchResult>>> {
        const request = this.buildSearchRequest(searchFilter);
        return this._ballotSearchService.searchBallots(request);
    }

    buildSearchRequest(searchFilter: Partial<ISearchFormData>): IBallotSearchRequest {
        // Save Filter
        localStorage.setItem(appConstants.localStorageKey.ballotSearch, JSON.stringify(searchFilter));

        let request: IBallotSearchRequest = {
            searchTerm: searchFilter.searchTermCtrl ? searchFilter.searchTermCtrl : '',
            ballotTypes: searchFilter.ballotTypeCtrl ? searchFilter.ballotTypeCtrl : [],
            statusIds: searchFilter.ballotStatusCtrl ? searchFilter.ballotStatusCtrl : [],
            committeeCodes: searchFilter.ballotSelectionCtrl != this.ballotSelectionType.allBallots ? this.currentUserCommitteeCodes : [],
            ballotReviewStages: searchFilter.ballotReviewStageCtrl ? searchFilter.ballotReviewStageCtrl : [],
            page: searchFilter.currentPage??1,
            limit: 50,
            isAscendingOrder: this.sort?.direction == "asc",
            orderBy: (this.sort?.active != undefined && this.sort?.direction != "") ? _.upperFirst(this.sort?.active) : "LastUpdatedDate"
        };

        return request;
    }

    subscribeSearchResults(obs$: Observable<IApiResponse<IPaginatedCollection<IBallotSearchResult>>>): void {
        this.loading = true;
        this.paginatedCollection = null;
        this.tableData = [];
        this.subscriptions.add(
            obs$.subscribe({
                next: (response) => {
                    this.loading = false;
                    this.paginatedCollection = response.content;
                    this.tableData = this.paginatedCollection.data;
                    this.getAllBallots();
                    this.checkToastMessage();
                },
                error: (error) => {
                    this.loading = false;
                    console.error(error);
                }
            })
        );
    }

    getAllBallots(): void {
      const request = this.buildSearchRequest(this.searchForm.value);
      request.limit = this.paginatedCollection?.totalRecords ?? 0;

      this.subscriptions.add(
        this._ballotSearchService.searchBallotsAllPages(request).subscribe({
          next: (response) => {
            this.exportData = response.content.data;
          },
          error: (error) => {
            console.error('Failed to load export data:', error);
          }
        })
      );
    }

    checkToastMessage(){
      const toastSuccessfulMessage = ToastMessage.successMessage;
      const toastMessageTitle = BallotTypes.SRS;
      const ballotCreated = localStorage.getItem('ballotCreated');
      if (ballotCreated) {
        this.searchForm.get('ballotSelectionCtrl')?.setValue(this.ballotSelectionType.allBallots);
        this._toastService.showSuccess(toastSuccessfulMessage, toastMessageTitle);
        localStorage.removeItem('ballotCreated');
      }
    }

    onPageChange(page: number): void {
        if (this.searchForm.valid) {
            let searchFilter = this.searchForm.value;
            const searchObs$ = this.buildSearchObservable({...searchFilter, currentPage: page});

            this.subscribeSearchResults(searchObs$);
        }
    }

    performSearchPostSort() : void {
        const savedFilters = localStorage.getItem(appConstants.localStorageKey.ballotSearch);
        if (savedFilters) {
            let searchFilter = JSON.parse(savedFilters);
            const searchObs$ = this.buildSearchObservable(searchFilter);
            this.subscribeSearchResults(searchObs$);
        }
    }

    onResetFilterClickEvent(): void {
        const defaultFilter = this.getDefaultSearchFilter();
        this.searchForm.reset(defaultFilter);
    }

    redirectToCreateBallotPage() {
        this._router.navigate(['/ballots/create']);
    }

    closeLegendPopover() {
        if (this.popover.isOpen())
            this.popover.close();
    }

    onRadioButtonChange(): void {
    }

    handleSort(sort: Sort) {
      this.sort = sort;
      this.performSearchPostSort();
    }
}

interface ISearchFormData {
  searchTermCtrl: string | null;
  ballotSelectionCtrl: string | null;
  ballotTypeCtrl: BallotType[] | null;
  ballotStatusCtrl: UnifiedBallotStatus[] | null;
  ballotReviewStageCtrl: (SrsBallotReviewStage | null)[] | null;
  currentPage: number | 1;
}
